import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { neonQuery } from '../services/api';
import { canonicalFloorName, isShortFloorName, normalizeTaktHistory, floorNameOptions } from '../utils/floorNames';

// ─── BRAND TOKENS (matches CPX admin portal) ──────────────────────────────────
const B = {
  navy: '#1a2e4a',
  navyLight: '#243755',
  orange: '#e8500a',
  orangeLight: '#f97316',
  cream: '#fdf8f3',
  white: '#ffffff',
  cardBg: '#ffffff',
  cardBorder: '#dde3ec',
  rowAlt: '#f8fafc',
  rowSep: '#e8edf5',
  textDark: '#1a2e4a',
  textMid: '#4a5568',
  textLight: '#8a9ab0',
  divider: '#e2e9f0',
  green: '#166534',
  greenBg: '#f0fdf4',
  greenBorder: '#bbf7d0',
  amber: '#b45309',
  amberBg: '#fefce8',
  amberBorder: '#fde68a',
  red: '#b91c1c',
  redBg: '#fef2f2',
  redBorder: '#fecaca',
  blue: '#1d4ed8',
  blueBg: '#eff6ff',
  blueBorder: '#bfdbfe',
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const r2 = n => Math.round(n * 100) / 100;
const fb = n => (Math.round(n * 100) / 100).toFixed(2);
const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDate = iso => {
  if (!iso) return '';
  return new Date(iso + 'T12:00:00').toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
  });
};
const fmtShortDate = iso => {
  if (!iso) return '';
  return new Date(iso + 'T12:00:00').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric'
  });
};

// ─── STORAGE KEY ─────────────────────────────────────────────────────────────
const FULFILLMENT_OPS_KEY = 'fulfillment_ops_v1';

// ─── SHARED UI ATOMS ──────────────────────────────────────────────────────────
function Card({ children, style = {} }) {
  return (
    <div style={{
      background: B.cardBg,
      border: `1px solid ${B.cardBorder}`,
      borderRadius: 12,
      overflow: 'hidden',
      boxShadow: '0 1px 6px rgba(26,46,74,.06)',
      ...style
    }}>
      {children}
    </div>
  );
}

function CardHeader({ children, sub, right }) {
  return (
    <div style={{
      padding: '12px 16px',
      borderBottom: `1px solid ${B.rowSep}`,
      background: B.rowAlt,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start'
    }}>
      <div>
        <div style={{ color: B.navy, fontWeight: 700, fontSize: 14 }}>{children}</div>
        {sub && <div style={{ color: B.textLight, fontSize: 11, marginTop: 2 }}>{sub}</div>}
      </div>
      {right && <div>{right}</div>}
    </div>
  );
}

function Inp({ value, onChange, placeholder = '', type = 'text', style = {}, ...rest }) {
  return (
    <input
      type={type}
      value={value == null ? '' : value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        width: '100%',
        padding: '7px 10px',
        borderRadius: 7,
        border: `1px solid ${B.cardBorder}`,
        fontSize: 13,
        color: B.textDark,
        background: B.white,
        outline: 'none',
        boxSizing: 'border-box',
        ...style
      }}
      {...rest}
    />
  );
}

function Btn({ onClick, children, variant = 'primary', small = false, style = {} }) {
  const base = {
    border: 'none', borderRadius: 8, cursor: 'pointer',
    fontWeight: 700, fontSize: small ? 12 : 13,
    padding: small ? '6px 12px' : '9px 18px', ...style
  };
  const variants = {
    primary: { background: B.navy, color: B.white },
    orange: { background: B.orange, color: B.white },
    ghost: { background: B.rowAlt, color: B.textMid, border: `1px solid ${B.cardBorder}` },
    danger: { background: B.redBg, color: B.red, border: `1px solid ${B.redBorder}` },
    green: { background: B.greenBg, color: B.green, border: `1px solid ${B.greenBorder}` },
  };
  return <button onClick={onClick} style={{ ...base, ...variants[variant] }}>{children}</button>;
}

// ─── TABS ─────────────────────────────────────────────────────────────────────
const TABS = [
  { val: 'snapshot', label: '📊 Daily Snapshot' },
  { val: 'takt', label: '📋 Fulfillment TAKT' },
  { val: 'processing', label: '🏭 Processing Planner' },
  { val: 'email', label: '✉️ Email' },
];

function OpsTabBar({ tab, onChange }) {
  return (
    <div style={{
      display: 'flex', gap: 3, background: B.rowAlt,
      borderRadius: 10, padding: 4, marginBottom: 20,
      border: `1px solid ${B.cardBorder}`, flexWrap: 'wrap'
    }}>
      {TABS.map(t => (
        <button key={t.val} onClick={() => onChange(t.val)} style={{
          flex: '1 1 auto', padding: '9px 8px', border: 'none', borderRadius: 8,
          cursor: 'pointer', fontWeight: 700, fontSize: 12,
          background: tab === t.val ? B.navy : 'transparent',
          color: tab === t.val ? B.white : B.textMid,
          boxShadow: tab === t.val ? '0 2px 8px rgba(26,46,74,.2)' : 'none',
          transition: 'all .15s', whiteSpace: 'nowrap'
        }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ─── DAILY SNAPSHOT TAB (mirrors HCD Demand Planning layout) ──────────────────
function DailySnapshotTab() {
  const [hcpState, setHcpState] = useState(null);
  const [loading, setLoading] = useState(true);
  const today = todayISO();

  useEffect(() => {
    neonQuery('hcp_state_load_latest', {})
      .then(res => {
        const row = Array.isArray(res) ? res[0] : (res?.rows?.[0] ?? null);
        if (row?.state) setHcpState(row.state);
      })
      .catch(err => console.warn('[OpsPlanner] HCP state load failed:', err))
      .finally(() => setLoading(false));
  }, []);

  const dateLabel = fmtDate(today);

  if (loading) return (
    <div style={{ padding: '40px 0', textAlign: 'center', color: B.textLight, fontSize: 13 }}>Loading snapshot data…</div>
  );

  if (!hcpState) return (
    <div style={{
      background: `linear-gradient(135deg, ${B.navy} 0%, #1e3a5f 100%)`,
      borderRadius: 12, padding: '20px', marginBottom: 16
    }}>
      <div style={{ color: '#fff', fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Daily Ops Snapshot</div>
      <div style={{ color: 'rgba(255,255,255,.5)', fontSize: 12 }}>{dateLabel}</div>
      <div style={{ marginTop: 20, padding: '24px', background: 'rgba(255,255,255,.07)', borderRadius: 10, textAlign: 'center', color: 'rgba(255,255,255,.5)', fontSize: 13 }}>
        No HCD data yet — open the HCD Demand Planning tool to populate today's inventory and demand.
      </div>
    </div>
  );

  const SIZES = ['Colossal', 'Premium', 'Select', 'Standard', 'Large Female', 'Regular Female'];
  const SIZE_COLORS = {
    Colossal: '#1d6fa8', Premium: '#2e7d52', Select: '#a16207',
    Standard: '#b91c1c', 'Large Female': '#b45309', 'Regular Female': '#6d28d9'
  };
  const UPGRADE_ACTION = {
    Colossal: 'Upgrade Premium → Colossal',
    Premium: 'Upgrade Select orders → Premium',
    Select: 'Upgrade Standard orders → Select',
    Standard: 'CR: Standard short — offer alternatives',
    'Large Female': 'CR: Large Female short — offer alternatives',
    'Regular Female': 'CR: Offer to Large Female w/ credit',
  };

  const inventoryLots = hcpState.inventoryLots || [];
  const demandBySize = hcpState.demandOverrides || {};

  const enriched = inventoryLots.map(lot => {
    const age = Math.round((new Date(today + 'T12:00:00') - new Date((lot.steamDate || today) + 'T12:00:00')) / 86400000);
    return { ...lot, age, usable: age <= 9, status: age <= 5 ? 'FRESH' : age <= 7 ? 'AGING' : age <= 9 ? 'URGENT' : 'EXPIRED' };
  });

  // Build per-size inventory summary
  const bySize = {};
  SIZES.forEach(s => { bySize[s] = { usable: 0, d7: 0, d8: 0, d9: 0, expired: 0 }; });
  let totalUsable = 0, totalUrgent = 0, totalExpired = 0;
  enriched.forEach(lot => {
    const bu = Number(lot.bushels) || 0;
    if (lot.usable) {
      totalUsable = r2(totalUsable + bu);
      if (bySize[lot.size]) {
        bySize[lot.size].usable = r2(bySize[lot.size].usable + bu);
        if (lot.age === 7) bySize[lot.size].d7 = r2(bySize[lot.size].d7 + bu);
        if (lot.age === 8) bySize[lot.size].d8 = r2(bySize[lot.size].d8 + bu);
        if (lot.age === 9) bySize[lot.size].d9 = r2(bySize[lot.size].d9 + bu);
      }
      if (lot.age >= 8) totalUrgent = r2(totalUrgent + bu);
    } else {
      totalExpired = r2(totalExpired + bu);
      if (bySize[lot.size]) bySize[lot.size].expired = r2(bySize[lot.size].expired + bu);
    }
  });

  // Committed outgoing = demand bushels for each size (from demandOverrides or salesPace)
  // salesPaceBySize may be stored in hcpState — try a few field names
  const salesPace = hcpState.salesPaceBySize || hcpState.demandBySizeToday || {};
  const getOut = (size) => {
    const d = demandBySize[size];
    const s = salesPace[size];
    if (d != null && !isNaN(Number(d))) return Number(d);
    if (s != null && !isNaN(Number(s))) return Number(s);
    return null;
  };

  // Inventory pressure = aging sizes (d7+)
  const pressureSizes = SIZES.filter(s => bySize[s].d8 > 0 || bySize[s].d9 > 0 || bySize[s].d7 > 0);

  // Shortages = sizes where outgoing > on-hand (or outgoing > 0 and on-hand = 0)
  const shortageSizes = SIZES.filter(s => {
    const out = getOut(s);
    const onHand = bySize[s].usable;
    if (out == null) return false;
    return out > onHand;
  });

  // Today's plan — generated from pressure + shortages
  const planItems = [];
  // Upgrade actions for each pressure size
  pressureSizes.forEach(size => {
    const s = bySize[size];
    const isUrgent = s.d8 > 0 || s.d9 > 0;
    const atRisk = r2((s.d8 || 0) + (s.d9 || 0) + (s.d7 || 0));
    planItems.push({
      type: 'upgrade',
      color: isUrgent ? '#1d4ed8' : '#1d4ed8',
      text: `${UPGRADE_ACTION[size]} — ${fb(atRisk)} bu at risk`,
    });
  });
  // CR actions for shortages
  shortageSizes.forEach(size => {
    if (pressureSizes.includes(size)) return; // already covered
    planItems.push({
      type: 'cr',
      color: '#c2540a',
      text: UPGRADE_ACTION[size] || `CR: ${size} short — offer alternatives`,
    });
  });

  // Badge counts
  const agingCount = pressureSizes.length;
  const shortCount = shortageSizes.length;
  const actionCount = planItems.length;

  return (
    <div>
      {/* Dark header */}
      <div style={{
        background: `linear-gradient(135deg, ${B.navy} 0%, #1e3a5f 100%)`,
        borderRadius: 12, padding: '16px 20px', marginBottom: 16,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8
      }}>
        <div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>Daily Ops Snapshot</div>
          <div style={{ color: 'rgba(255,255,255,.5)', fontSize: 12, marginTop: 2 }}>{dateLabel}</div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {agingCount > 0 && (
            <div style={{ background: 'rgba(253,186,116,.2)', border: '1px solid rgba(253,186,116,.4)', borderRadius: 8, padding: '5px 12px', color: '#fdba74', fontSize: 11, fontWeight: 700 }}>
              {agingCount} aging
            </div>
          )}
          {shortCount > 0 && (
            <div style={{ background: 'rgba(252,165,165,.2)', border: '1px solid rgba(252,165,165,.4)', borderRadius: 8, padding: '5px 12px', color: '#fca5a5', fontSize: 11, fontWeight: 700 }}>
              {shortCount} short
            </div>
          )}
          {actionCount > 0 && (
            <div style={{ background: 'rgba(134,239,172,.2)', border: '1px solid rgba(134,239,172,.4)', borderRadius: 8, padding: '5px 12px', color: '#86efac', fontSize: 11, fontWeight: 700 }}>
              {actionCount} actions
            </div>
          )}
        </div>
      </div>

      {/* 2×2 grid — mirrors HCD */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {/* TOP LEFT: Inventory Pressure */}
        <Card style={{ padding: 0 }}>
          <div style={{
            background: agingCount > 0 ? '#fef9c3' : B.greenBg,
            borderBottom: `1px solid ${agingCount > 0 ? '#fde68a' : B.greenBorder}`,
            borderRadius: '10px 10px 0 0',
            padding: '10px 14px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ fontSize: 14 }}>{agingCount > 0 ? '⚠️' : '✅'}</span>
              <span style={{ fontWeight: 700, fontSize: 13, color: B.textDark }}>Inventory Pressure</span>
            </div>
            {agingCount > 0 && (
              <span style={{ fontSize: 11, fontWeight: 700, background: '#fde68a', color: '#92400e', borderRadius: 10, padding: '2px 8px' }}>{agingCount}</span>
            )}
          </div>
          <div style={{ padding: '10px 14px', minHeight: 80 }}>
            {pressureSizes.length === 0 ? (
              <div style={{ color: B.textLight, fontSize: 12, textAlign: 'center', padding: '14px 0' }}>No aging pressure today</div>
            ) : pressureSizes.map(size => {
              const s = bySize[size];
              const sc = SIZE_COLORS[size];
              const isUrgent = s.d8 > 0 || s.d9 > 0;
              return (
                <div key={size} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 0', borderBottom: `1px solid ${B.rowSep}`,
                  '&:last-child': { borderBottom: 'none' }
                }}>
                  <span style={{ fontSize: 13 }}>{s.d9 > 0 ? '🔴' : s.d8 > 0 ? '🟠' : '🟡'}</span>
                  <span style={{ fontWeight: 700, fontSize: 13, color: sc, flex: 1 }}>{size}</span>
                  <div style={{ textAlign: 'right', fontSize: 11, color: B.textMid }}>
                    {s.d9 > 0 && <div>D9:{fb(s.d9)}</div>}
                    {s.d8 > 0 && <div>D8:{fb(s.d8)}</div>}
                    {s.d7 > 0 && !isUrgent && <div>D7:{fb(s.d7)}</div>}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: isUrgent ? '#c2540a' : '#a16207' }}>{fb(s.d7 + s.d8 + s.d9)} bu</span>
                </div>
              );
            })}
          </div>
        </Card>

        {/* TOP RIGHT: Committed Outgoing Today */}
        <Card style={{ padding: 0 }}>
          <div style={{
            background: B.rowAlt,
            borderBottom: `1px solid ${B.cardBorder}`,
            borderRadius: '10px 10px 0 0',
            padding: '10px 14px',
            display: 'flex', alignItems: 'center', gap: 7
          }}>
            <span style={{ fontSize: 14 }}>📦</span>
            <span style={{ fontWeight: 700, fontSize: 13, color: B.textDark }}>Committed Outgoing Today</span>
          </div>
          <div style={{ padding: '10px 14px', minHeight: 80 }}>
            {SIZES.filter(s => bySize[s].usable > 0 || getOut(s) != null).map(size => {
              const s = bySize[size];
              const out = getOut(size);
              const sc = SIZE_COLORS[size];
              const onHand = s.usable;
              const covered = out == null || onHand >= out;
              return (
                <div key={size} style={{
                  display: 'grid', gridTemplateColumns: '10px 1fr auto auto auto',
                  alignItems: 'center', gap: 6,
                  padding: '7px 0', borderBottom: `1px solid ${B.rowSep}`
                }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: sc }} />
                  <span style={{ fontWeight: 600, fontSize: 12, color: B.textDark }}>{size}</span>
                  <span style={{ fontSize: 11, color: B.textMid }}>on hand: <strong>{fb(onHand)} bu</strong></span>
                  {out != null && <span style={{ fontSize: 11, color: B.textMid }}>out: <strong>{fb(out)} bu</strong></span>}
                  {out != null ? (
                    <span style={{ fontSize: 13 }}>{covered ? '✓' : '⚠️'}</span>
                  ) : <span />}
                </div>
              );
            })}
            {SIZES.filter(s => bySize[s].usable > 0 || getOut(s) != null).length === 0 && (
              <div style={{ color: B.textLight, fontSize: 12, textAlign: 'center', padding: '14px 0' }}>No inventory data</div>
            )}
          </div>
        </Card>

        {/* BOTTOM LEFT: Shortages */}
        <Card style={{ padding: 0 }}>
          <div style={{
            background: shortCount > 0 ? '#fff7ed' : B.greenBg,
            borderBottom: `1px solid ${shortCount > 0 ? '#fed7aa' : B.greenBorder}`,
            borderRadius: '10px 10px 0 0',
            padding: '10px 14px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ fontSize: 14 }}>{shortCount > 0 ? '▼' : '✅'}</span>
              <span style={{ fontWeight: 700, fontSize: 13, color: B.textDark }}>Shortages</span>
            </div>
            {shortCount > 0 && (
              <span style={{ fontSize: 11, fontWeight: 700, background: '#fed7aa', color: '#9a3412', borderRadius: 10, padding: '2px 8px' }}>{shortCount}</span>
            )}
          </div>
          <div style={{ padding: '10px 14px', minHeight: 80 }}>
            {shortageSizes.length === 0 ? (
              <div style={{ color: B.textLight, fontSize: 12, textAlign: 'center', padding: '14px 0' }}>No shortages</div>
            ) : shortageSizes.map(size => {
              const sc = SIZE_COLORS[size];
              return (
                <div key={size} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 0', borderBottom: `1px solid ${B.rowSep}`
                }}>
                  <span style={{ fontSize: 13, color: '#c2540a' }}>▼</span>
                  <span style={{ fontWeight: 700, fontSize: 13, color: sc, flex: 1 }}>{size}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#c2540a' }}>short</span>
                </div>
              );
            })}
          </div>
        </Card>

        {/* BOTTOM RIGHT: Today's Plan */}
        <Card style={{ padding: 0 }}>
          <div style={{
            background: actionCount > 0 ? '#f0fdf4' : B.rowAlt,
            borderBottom: `1px solid ${actionCount > 0 ? B.greenBorder : B.cardBorder}`,
            borderRadius: '10px 10px 0 0',
            padding: '10px 14px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ fontSize: 14 }}>📋</span>
              <span style={{ fontWeight: 700, fontSize: 13, color: B.textDark }}>Today's Plan</span>
            </div>
            {actionCount > 0 && (
              <span style={{ fontSize: 11, fontWeight: 700, background: B.greenBorder, color: '#166534', borderRadius: 10, padding: '2px 8px' }}>{actionCount}</span>
            )}
          </div>
          <div style={{ padding: '10px 14px', minHeight: 80 }}>
            {planItems.length === 0 ? (
              <div style={{ color: B.textLight, fontSize: 12, textAlign: 'center', padding: '14px 0' }}>No actions needed — inventory looks clean</div>
            ) : planItems.map((item, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'flex-start', gap: 8,
                padding: '6px 0', borderBottom: i < planItems.length - 1 ? `1px solid ${B.rowSep}` : 'none'
              }}>
                <div style={{
                  width: 8, height: 8, borderRadius: '50%', marginTop: 4, flexShrink: 0,
                  background: item.type === 'upgrade' ? B.blue : '#c2540a'
                }} />
                <span style={{ fontSize: 12, color: B.textDark, lineHeight: 1.4 }}>{item.text}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}


// ─── FULFILLMENT TAKT TAB ─────────────────────────────────────────────────────
const OPH_GOAL = 20;

function makePackerLine(o = {}) {
  return {
    id: `pl-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
    lineNum: '',
    name: '',
    orderspacked: '',
    duration: '',
    ...o
  };
}

function makeTaktDay() {
  return { lines: [], projectedOrders: '', actualOrders: '', notes: '' };
}

const LINE_NUMS = ['1', '2', '3', '4'];
const LINE_COLORS = { '1': '#1d6fa8', '2': '#2e7d52', '3': '#a16207', '4': '#7c3aed' };

// ─── MEASURED NUMBERS (written by the packing floor, read-only here) ──────────
//
// Carman, 2026-09-02: the OPH was calculating correctly on the packing-floor
// board and landing nowhere she could pull it from. This panel is that number,
// on this page.
//
// ⚠️ READ-ONLY BY DESIGN, and it must stay that way. `taktAuto` is owned by
// netlify/functions/lib/takt-autofill.js; this page owns taktAll/taktHistory.
// The page autosaves its whole state on a 3-second debounce, so if these
// numbers were merged into the editable table automatically, a keystroke would
// save a stale copy straight over them. Filling the table is therefore a
// deliberate button press, which also keeps a human between the measurement and
// the award history — packer initials are mapped to people through a table
// nothing in the data can verify, so a wrong row would silently move one
// packer's work onto another's name.
const fmtClock = iso => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York'
    });
  } catch (_) { return ''; }
};

function MeasuredNumbersPanel({ day, selDate, hasLines, onFill }) {
  if (!day) {
    return (
      <Card style={{ marginBottom: 16, padding: '14px 16px' }}>
        <div style={{ color: B.navy, fontWeight: 700, fontSize: 14 }}>Measured on the packing floor</div>
        <div style={{ color: B.textLight, fontSize: 12, marginTop: 4 }}>
          {/* "We have not been told yet" and "nobody packed" are different facts and
              must never render the same way. */}
          No measured numbers recorded for {fmtShortDate(selDate)} yet. They are written
          every 10 minutes through the packing day, once orders have been packed and
          attributed to a packer.
        </div>
      </Card>
    );
  }

  const lines = day.lines || [];
  const rated = lines.filter(l => typeof l.oph === 'number' && l.oph > 0);

  return (
    <Card style={{ marginBottom: 16, padding: 0, overflow: 'hidden' }}>
      <div style={{ background: B.navy, padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>Measured on the packing floor</div>
          <div style={{ color: 'rgba(255,255,255,.45)', fontSize: 11, marginTop: 1 }}>
            Orders counted off the live shipping report · hours from the TimeClock Plus Packing job code
          </div>
        </div>
        <div style={{ color: 'rgba(255,255,255,.6)', fontSize: 11, textAlign: 'right' }}>
          {day.generatedAt ? <>as of {fmtClock(day.generatedAt)}</> : null}
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', background: B.rowAlt, borderBottom: `1px solid ${B.rowSep}` }}>
          {['Packer', 'Orders', 'Hours', 'OPH'].map((h, i) => (
            <div key={h} style={{ padding: '7px 12px', color: B.textLight, fontSize: 10, fontWeight: 700, letterSpacing: .6, textTransform: 'uppercase', textAlign: i === 0 ? 'left' : 'center' }}>{h}</div>
          ))}
        </div>
        {lines.map((l, i) => (
          <div key={l.raw || i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', borderBottom: `1px solid ${B.rowSep}`, background: i % 2 ? B.rowAlt : B.white, alignItems: 'center' }}>
            <div style={{ padding: '8px 12px', fontSize: 13, fontWeight: 600, color: B.textDark }}>
              {l.name}
              {/* An unrecognised packer is SHOWN, never dropped (Carman's explicit
                  ask on the TV board). The chip says the initials have no person
                  against them yet, so the count is still theirs. */}
              {!l.mapped && (
                <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, background: B.orange, color: '#fff', borderRadius: 4, padding: '1px 6px', verticalAlign: 'middle' }}>
                  INITIALS ONLY
                </span>
              )}
            </div>
            <div style={{ padding: '8px 12px', textAlign: 'center', fontSize: 14, fontWeight: 700, color: B.textDark }}>{l.orders}</div>
            <div style={{ padding: '8px 12px', textAlign: 'center', fontSize: 13, color: l.hours == null ? B.textLight : B.textMid }}>
              {l.hours == null ? '—' : fb(l.hours)}
            </div>
            <div style={{ padding: '8px 12px', textAlign: 'center' }}>
              {typeof l.oph === 'number' && l.oph > 0 ? (
                <span style={{ fontWeight: 800, fontSize: 15, color: l.oph >= OPH_GOAL ? B.green : B.amber }}>{l.oph}</span>
              ) : (
                // These two are different problems and a lead reading the page
                // needs to tell them apart: one is a missing punch, the other is
                // simply too early in the shift for a rate to mean anything.
                <span style={{ fontSize: 10, color: B.textLight, fontWeight: 600 }}>
                  {l.rateState === 'no-hours' ? 'not clocked in' : 'too early'}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div style={{ padding: '10px 14px', background: B.rowAlt, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', borderTop: `1px solid ${B.rowSep}` }}>
        <div style={{ fontSize: 12, color: B.textMid }}>
          <strong style={{ color: B.textDark }}>{day.totalOrders}</strong> orders
          {' · '}<strong style={{ color: B.textDark }}>{fb(day.totalHours || 0)}</strong> hrs
          {' · Team '}
          <strong style={{ color: day.teamOph != null && day.teamOph >= OPH_GOAL ? B.green : B.amber }}>
            {day.teamOph != null ? day.teamOph : '—'}
          </strong> OPH
          {rated.length < lines.length && (
            <span style={{ color: B.textLight }}> (from {rated.length} of {lines.length})</span>
          )}
        </div>
        {/* The numbers on screen must add up to the packed count. An order packed
            but not yet attributed is a real state (production has not been
            re-read yet, or the packer field is blank upstream), and hiding it
            would leave the totals quietly short. */}
        {day.unattributed > 0 && (
          <div style={{ fontSize: 11, color: B.amber, background: B.amberBg, border: `1px solid ${B.amberBorder}`, borderRadius: 6, padding: '3px 8px' }}>
            {day.attributed} of {day.packedTotal} packed orders attributed · {day.unattributed} not yet
          </div>
        )}
        <button
          onClick={() => {
            if (hasLines && !window.confirm('Replace the lines already in the TAKT table with these measured numbers?')) return;
            onFill(lines);
          }}
          style={{ marginLeft: 'auto', padding: '7px 14px', background: B.orange, color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
          Fill the table with these numbers
        </button>
      </div>
    </Card>
  );
}

function TaktLineTable({ lines, onUpdate, onAdd, onRemove }) {
  return (
    <div>
      {lines.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          {/* Header */}
          <div style={{ display: 'grid', gridTemplateColumns: '64px 2fr 1.2fr 1.2fr 1.2fr 36px', background: B.navy, borderRadius: '8px 8px 0 0' }}>
            {['Line #', 'Packer Name', 'Orders Packed', 'Duration (hrs)', 'OPH', ''].map((h, i) => (
              <div key={i} style={{ padding: '8px 10px', color: 'rgba(255,255,255,.5)', fontSize: 10, fontWeight: 700, letterSpacing: .6, textTransform: 'uppercase', textAlign: i >= 2 && i <= 4 ? 'center' : 'left' }}>{h}</div>
            ))}
          </div>
          {lines.map((line, ri) => {
            const ordersVal = parseFloat(line.orderspacked) || 0;
            const dur = parseFloat(line.duration) || 0;
            const oph = ordersVal > 0 && dur > 0 ? r2(ordersVal / dur) : null;
            const ophPct = oph != null ? Math.round((oph / OPH_GOAL) * 100) : null;
            const lineColor = LINE_COLORS[line.lineNum] || B.textLight;
            return (
              <div key={line.id} style={{
                display: 'grid', gridTemplateColumns: '64px 2fr 1.2fr 1.2fr 1.2fr 36px',
                alignItems: 'center', background: ri % 2 === 0 ? B.white : B.rowAlt,
                border: `1px solid ${B.cardBorder}`, borderTop: 'none',
                borderLeft: `3px solid ${line.lineNum ? lineColor : B.cardBorder}`
              }}>
                {/* Line # selector */}
                <div style={{ padding: '6px 8px' }}>
                  <select
                    value={line.lineNum || ''}
                    onChange={e => onUpdate(line.id, 'lineNum', e.target.value)}
                    style={{
                      width: '100%', padding: '6px 4px', borderRadius: 7,
                      border: `1.5px solid ${line.lineNum ? lineColor : B.cardBorder}`,
                      fontSize: 14, fontWeight: 800, textAlign: 'center',
                      color: line.lineNum ? lineColor : B.textLight,
                      background: line.lineNum ? `${lineColor}12` : B.white,
                      outline: 'none', cursor: 'pointer', appearance: 'none',
                      WebkitAppearance: 'none'
                    }}
                  >
                    <option value="">—</option>
                    {LINE_NUMS.map(n => (
                      <option key={n} value={n}>L{n}</option>
                    ))}
                  </select>
                </div>
                {/* Packer name */}
                <div style={{ padding: '6px 8px' }}>
                  <Inp
                    value={line.name}
                    placeholder="First and last name"
                    list="floor-names"
                    autoComplete="off"
                    style={{ fontSize: 13, padding: '6px 8px', ...(isShortFloorName(line.name) ? { borderColor: B.amber } : {}) }}
                    onChange={e => onUpdate(line.id, 'name', e.target.value)}
                    onBlur={e => { const n = canonicalFloorName(e.target.value); if (n !== e.target.value) onUpdate(line.id, 'name', n); }}
                  />
                  {isShortFloorName(line.name) && (
                    <div style={{ fontSize: 10, color: B.amber, marginTop: 2 }}>Add the last name</div>
                  )}
                </div>
                {/* Orders packed */}
                <div style={{ padding: '6px 6px' }}>
                  <Inp type="number" value={line.orderspacked} placeholder="0" style={{ fontSize: 13, padding: '6px 8px', textAlign: 'center' }} onChange={e => onUpdate(line.id, 'orderspacked', e.target.value)} />
                </div>
                {/* Duration */}
                <div style={{ padding: '6px 6px' }}>
                  <Inp type="number" value={line.duration} placeholder="0" style={{ fontSize: 13, padding: '6px 8px', textAlign: 'center' }} onChange={e => onUpdate(line.id, 'duration', e.target.value)} />
                </div>
                {/* OPH */}
                <div style={{ padding: '6px 6px', textAlign: 'center' }}>
                  {oph != null ? (
                    <div>
                      <span style={{ fontWeight: 800, fontSize: 15, color: oph >= OPH_GOAL ? B.green : B.amber }}>{oph}</span>
                      {ophPct != null && <div style={{ fontSize: 10, color: oph >= OPH_GOAL ? B.green : B.amber }}>{ophPct}%</div>}
                    </div>
                  ) : <span style={{ color: B.textLight, fontSize: 13 }}>—</span>}
                </div>
                {/* Remove */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>
                  <button onClick={() => onRemove(line.id)} style={{ background: 'none', border: 'none', color: B.textLight, cursor: 'pointer', fontSize: 14, padding: 4, borderRadius: 4 }}>✕</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <datalist id="floor-names">
        {floorNameOptions().map(n => <option key={n} value={n} />)}
      </datalist>
      <button onClick={onAdd} style={{ width: '100%', padding: '10px', background: B.white, border: `2px dashed ${B.cardBorder}`, borderRadius: lines.length ? '0 0 8px 8px' : 8, color: B.textMid, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
        + Add Packer
      </button>
    </div>
  );
}

function FulfillmentTaktTab({ taktAll, onTaktAllChange, taktHistory, onHistoryChange, onSaveNow, taktAuto }) {
  const allData = taktAll || {};
  const history = taktHistory || [];
  const historyRef = useRef(history);
  historyRef.current = history;

  const [selDate, setSelDate] = useState(todayISO());

  function setAllData(updater) {
    const next = typeof updater === 'function' ? updater(allData) : updater;
    onTaktAllChange(next);
  }

  const day = allData[selDate] || makeTaktDay();
  const lines = day.lines || [];

  function setDay(updater) {
    setAllData(all => {
      const cur = all[selDate] || makeTaktDay();
      const next = typeof updater === 'function' ? updater(cur) : { ...cur, ...updater };
      return { ...all, [selDate]: next };
    });
  }

  function addLine() {
    setDay(d => ({ ...d, lines: [...d.lines, makePackerLine()] }));
  }
  function removeLine(id) {
    setDay(d => ({ ...d, lines: d.lines.filter(l => l.id !== id) }));
  }
  function updateLine(id, field, val) {
    setDay(d => ({ ...d, lines: d.lines.map(l => l.id === id ? { ...l, [field]: val } : l) }));
  }

  // Derived totals
  const totalOrders = lines.reduce((s, l) => s + (parseFloat(l.orderspacked) || 0), 0);
  const totalHrs = lines.reduce((s, l) => s + (parseFloat(l.duration) || 0), 0);
  const lineOPHs = lines.map(l => {
    const o = parseFloat(l.orderspacked) || 0;
    const h = parseFloat(l.duration) || 0;
    return o > 0 && h > 0 ? r2(o / h) : null;
  }).filter(v => v !== null);
  const teamOPH = lineOPHs.length > 0 ? r2(lineOPHs.reduce((s, v) => s + v, 0) / lineOPHs.length) : null;

  const projOrders = parseFloat(day.projectedOrders) || 0;
  const actOrders = parseFloat(day.actualOrders) || totalOrders;
  const projOPH = OPH_GOAL;
  const actOPH = teamOPH;
  const ordPct = projOrders > 0 && actOrders > 0 ? Math.round(actOrders / projOrders * 100) : null;
  const ophPct = actOPH != null ? Math.round(actOPH / projOPH * 100) : null;

  const savedDates = Object.keys(allData).filter(d => (allData[d]?.lines?.length > 0)).sort().reverse();

  // Copy the measured numbers into the editable table. A deliberate press, not
  // an automatic merge — see MeasuredNumbersPanel for why.
  //
  // A packer with no usable rate is still carried across with their order count
  // and a blank duration, so the row is there to finish rather than missing.
  // commitHistory() already skips lines without both figures, so an unfinished
  // row cannot reach the award history by accident.
  function fillFromMeasured(measured) {
    setDay(d => ({
      ...d,
      lines: measured.map((m, i) => makePackerLine({
        lineNum: LINE_NUMS[i] || '',
        name: m.name,
        orderspacked: String(m.orders ?? ''),
        duration: m.hours == null ? '' : String(r2(m.hours)),
      })),
    }));
    if (onSaveNow) setTimeout(onSaveNow, 100);
  }

  function commitHistory() {
    const validLines = lines.filter(l => l.name.trim() && (parseFloat(l.orderspacked) || 0) > 0 && (parseFloat(l.duration) || 0) > 0);
    if (!validLines.length) { window.alert('No lines with name, orders, and hours filled in.'); return; }
    // A first name or nickname would start a second row for the same person on
    // the leaderboard — see utils/floorNames.js.
    const short = validLines.filter(l => isShortFloorName(l.name)).map(l => l.name.trim());
    if (short.length) { window.alert(`Use the full first and last name for: ${short.join(', ')}.\n\nPick the name from the list, or type both names.`); return; }
    const newEntries = validLines.map(l => {
      const orders = parseFloat(l.orderspacked) || 0;
      const hrs = parseFloat(l.duration) || 0;
      return {
        id: `ph-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
        date: selDate, name: canonicalFloorName(l.name), orders, hours: hrs, oph: r2(orders / hrs)
      };
    });
    const updatedHistory = [
      ...historyRef.current.filter(h => !(h.date === selDate && newEntries.some(e => e.name === canonicalFloorName(h.name)))),
      ...newEntries
    ];
    onHistoryChange(updatedHistory);
    if (onSaveNow) setTimeout(onSaveNow, 100);
    window.alert(`Performance saved for ${newEntries.length} line${newEntries.length !== 1 ? 's' : ''}.`);
  }

  return (
    <div>
      {/* Date selector */}
      <Card style={{ marginBottom: 16, padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: B.navy, fontWeight: 700, fontSize: 15 }}>Order Fulfillment TAKT</div>
            <div style={{ color: B.textLight, fontSize: 12, marginTop: 2 }}>Daily packing performance · OPH goal: {OPH_GOAL}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: B.textLight, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '.5px' }}>Report Date</div>
              <Inp type="date" value={selDate} onChange={e => setSelDate(e.target.value)} style={{ fontSize: 13, padding: '6px 10px', minWidth: 150 }} />
            </div>
            {savedDates.length > 1 && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: B.textLight, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '.5px' }}>Past Reports</div>
                <select value={selDate} onChange={e => setSelDate(e.target.value)} style={{ padding: '6px 10px', borderRadius: 7, border: `1px solid ${B.cardBorder}`, fontSize: 13, color: B.textDark, outline: 'none' }}>
                  {savedDates.map(d => (
                    <option key={d} value={d}>{new Date(d + 'T12:00:00').toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* What the floor actually did, measured rather than typed. Sits above the
          planner's own summary on purpose: it is the number Carman came for. */}
      <MeasuredNumbersPanel
        day={(taktAuto || {})[selDate]}
        selDate={selDate}
        hasLines={lines.length > 0}
        onFill={fillFromMeasured}
      />

      {/* Summary bar — mirrors Excel layout */}
      <Card style={{ marginBottom: 16, padding: 0, overflow: 'hidden' }}>
        {/* Column headers */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', background: B.navy }}>
          {['Projected Orders', 'Actual Orders', 'Projected OPH', 'Actual OPH'].map((h, i) => (
            <div key={h} style={{
              padding: '8px 12px', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,.5)',
              textTransform: 'uppercase', letterSpacing: '.5px', textAlign: 'center',
              borderRight: i < 3 ? '1px solid rgba(255,255,255,.08)' : 'none'
            }}>{h}</div>
          ))}
        </div>
        {/* Values row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr' }}>
          {/* Projected Orders — editable */}
          <div style={{ padding: '14px 12px', textAlign: 'center', borderRight: `1px solid ${B.rowSep}`, borderBottom: `1px solid ${B.rowSep}` }}>
            <Inp
              type="number"
              value={day.projectedOrders}
              placeholder="200"
              style={{ fontSize: 22, fontWeight: 800, textAlign: 'center', padding: '4px 6px', color: B.navy, border: `1.5px solid ${B.cardBorder}`, maxWidth: 100 }}
              onChange={e => setDay(d => ({ ...d, projectedOrders: e.target.value }))}
            />
            <div style={{ fontSize: 10, color: B.textLight, marginTop: 4 }}>scheduled today</div>
          </div>
          {/* Actual Orders — auto from lines */}
          <div style={{ padding: '14px 12px', textAlign: 'center', borderRight: `1px solid ${B.rowSep}`, borderBottom: `1px solid ${B.rowSep}` }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: actOrders > 0 ? (projOrders > 0 && actOrders >= projOrders ? B.green : B.navy) : B.textLight }}>
              {actOrders > 0 ? actOrders : '—'}
            </div>
            {projOrders > 0 && actOrders > 0 && (
              <div style={{ fontSize: 11, fontWeight: 700, color: actOrders >= projOrders ? B.green : B.amber, marginTop: 2 }}>
                {Math.round(actOrders / projOrders * 100)}% of goal
              </div>
            )}
            <div style={{ fontSize: 10, color: B.textLight, marginTop: 2 }}>packed (from lines)</div>
          </div>
          {/* Projected OPH */}
          <div style={{ padding: '14px 12px', textAlign: 'center', borderRight: `1px solid ${B.rowSep}`, borderBottom: `1px solid ${B.rowSep}` }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: B.textMid }}>{OPH_GOAL}</div>
            <div style={{ fontSize: 10, color: B.textLight, marginTop: 4 }}>goal</div>
          </div>
          {/* Actual OPH */}
          <div style={{ padding: '14px 12px', textAlign: 'center', borderBottom: `1px solid ${B.rowSep}` }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: actOPH != null ? (actOPH >= OPH_GOAL ? B.green : B.amber) : B.textLight }}>
              {actOPH != null ? actOPH : '—'}
            </div>
            {ophPct != null && (
              <div style={{ fontSize: 11, fontWeight: 700, color: actOPH >= OPH_GOAL ? B.green : B.amber, marginTop: 2 }}>
                {ophPct}% of goal
              </div>
            )}
            <div style={{ fontSize: 10, color: B.textLight, marginTop: 2 }}>avg across lines</div>
          </div>
        </div>
        {/* Sub-label */}
        <div style={{ padding: '7px 14px', background: B.rowAlt, borderTop: `1px solid ${B.rowSep}` }}>
          <span style={{ fontSize: 11, color: B.textLight, fontWeight: 600 }}>TAKT Report</span>
          <span style={{ fontSize: 11, color: B.textLight }}> · Daily packing performance · OPH goal: {OPH_GOAL}</span>
        </div>
      </Card>

      {/* Line table */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ background: B.navy, padding: '10px 16px', borderRadius: '10px 10px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>Line Performance</div>
            <div style={{ color: 'rgba(255,255,255,.45)', fontSize: 11, marginTop: 1 }}>Select line (1–4) · enter packer name · OPH auto-calculated · goal: {OPH_GOAL}</div>
          </div>
          {lines.length > 0 && (
            <div style={{ color: 'rgba(255,255,255,.7)', fontSize: 12, textAlign: 'right' }}>
              <div style={{ fontWeight: 700, fontSize: 18, color: '#86efac' }}>{totalOrders}</div>
              <div>total orders</div>
            </div>
          )}
        </div>
        <div style={{ padding: '14px 16px' }}>
          <TaktLineTable lines={lines} onUpdate={updateLine} onAdd={addLine} onRemove={removeLine} />
          {lines.length > 0 && (
            <div style={{ marginTop: 10, padding: '10px 14px', background: B.navy, borderRadius: 8, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              {[{ label: 'Total Orders', value: totalOrders > 0 ? String(totalOrders) : '—', color: '#86efac' },
                { label: 'Total Hours', value: totalHrs > 0 ? `${r2(totalHrs)} hrs` : '—', color: '#bfdbfe' },
                { label: 'Team OPH', value: teamOPH != null ? `${teamOPH}` : '—', color: '#fdba74' }
              ].map(c => (
                <div key={c.label} style={{ textAlign: 'center' }}>
                  <div style={{ color: 'rgba(255,255,255,.4)', fontSize: 9, fontWeight: 700, letterSpacing: .7, textTransform: 'uppercase', marginBottom: 4 }}>{c.label}</div>
                  <div style={{ color: c.value === '—' ? 'rgba(255,255,255,.2)' : c.color, fontWeight: 800, fontSize: 18 }}>{c.value}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Notes */}
      <Card style={{ marginBottom: 16 }}>
        <CardHeader>Notes</CardHeader>
        <div style={{ padding: '10px 16px 14px' }}>
          <textarea rows={3} value={day.notes || ''} placeholder="Operational notes for today's packing run…" onChange={e => setDay(d => ({ ...d, notes: e.target.value }))} style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: `1px solid ${B.cardBorder}`, fontSize: 12, color: B.textDark, resize: 'vertical', lineHeight: 1.5, outline: 'none', boxSizing: 'border-box' }} />
        </div>
      </Card>

      {/* Commit + clear */}
      <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center', marginBottom: 8 }}>
        <button onClick={commitHistory} style={{ background: B.navy, color: '#fff', border: 'none', borderRadius: 9, padding: '11px 28px', fontSize: 13, fontWeight: 700, cursor: 'pointer', width: '100%', maxWidth: 340 }}>
          ✓ Save Line Performance to History
        </button>
        <button onClick={() => { if (window.confirm(`Clear all data for ${selDate}?`)) setAllData(all => { const n = { ...all }; delete n[selDate]; return n; }); }} style={{ background: 'transparent', border: 'none', color: B.textLight, fontSize: 12, cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3 }}>
          Clear this day's report
        </button>
      </div>
    </div>
  );
}

// ─── OPH HISTORY (embedded inside TAKT, shown below commit button) ─────────────
function OphHistoryInline({ history, onClearAll, onClearDate, onDeleteEntry }) {
  const [period, setPeriod] = useState('month');
  const [showMonthPicker, setShowMonthPicker] = useState(false);

  // Build list of distinct months from history
  const availableMonths = useMemo(function() {
    var seen = {};
    (history || []).forEach(function(r) {
      if (!r.date) return;
      var key = r.date.slice(0, 7);
      if (!seen[key]) seen[key] = true;
    });
    return Object.keys(seen).sort().reverse();
  }, [history]);

  function periodLabel() {
    if (period === 'month') return 'This Month';
    if (period === 'ytd') return 'Year to Date';
    if (period === 'all') return 'All Time';
    if (period.length === 7) {
      var d = new Date(period + '-01T12:00:00');
      return d.toLocaleDateString([], { month: 'long', year: 'numeric' });
    }
    return period;
  }
  const [showClear, setShowClear] = useState(false);
  const now = new Date();

  const filtered = (history || []).filter(r => {
    if (!r.date || !r.name) return false;
    if (period === 'all') return true;
    if (period.length === 7) return r.date.slice(0, 7) === period;
    const d = new Date(r.date + 'T12:00:00');
    if (period === 'month') return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    if (period === 'ytd') return d.getFullYear() === now.getFullYear();
    return true;
  });

  const byPacker = {};
  filtered.forEach(r => {
    const n = canonicalFloorName(r.name);
    if (!n) return;
    if (!byPacker[n]) byPacker[n] = { name: n, days: 0, totalOrders: 0, totalHrs: 0, ophs: [], bestOPH: 0 };
    const g = byPacker[n];
    g.days++;
    g.totalOrders = r2(g.totalOrders + (parseFloat(r.orders) || 0));
    g.totalHrs = r2(g.totalHrs + (parseFloat(r.hours) || 0));
    const oph = r.oph ? parseFloat(r.oph) : (r.orders > 0 && r.hours > 0 ? r2(parseFloat(r.orders) / parseFloat(r.hours)) : 0);
    if (oph > 0) g.ophs.push(oph);
    if (oph > g.bestOPH) g.bestOPH = oph;
  });

  const rows = Object.values(byPacker).map(g => ({
    ...g,
    avgOPH: g.ophs.length ? r2(g.ophs.reduce((s, v) => s + v, 0) / g.ophs.length) : 0,
  })).sort((a, b) => b.avgOPH - a.avgOPH);

  const PERIOD_LABELS = { month: 'This Month', ytd: 'Year to Date', all: 'All Time' };
  const allDates = [...new Set((history || []).map(r => r.date))].sort().reverse();

  return (
    <Card style={{ marginTop: 20 }}>
      {/* Header */}
      <div style={{ background: B.navy, padding: '10px 16px', borderRadius: '10px 10px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>📈 OPH Performance History</div>
          <div style={{ color: 'rgba(255,255,255,.45)', fontSize: 11, marginTop: 1 }}>Leaderboard · goal: {OPH_GOAL} OPH</div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative' }}>
            <button onClick={() => { setShowMonthPicker(v => !v); setPeriod('month'); }} style={{ padding: '5px 12px', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 11, fontWeight: 700, background: (period === 'month' || period.length === 7) ? 'rgba(255,255,255,.2)' : 'rgba(255,255,255,.07)', color: (period === 'month' || period.length === 7) ? '#fff' : 'rgba(255,255,255,.5)' }}>
              {periodLabel()} {availableMonths.length > 1 ? '▾' : ''}
            </button>
            {showMonthPicker && availableMonths.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 99, background: '#fff', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,.15)', minWidth: 160, marginTop: 4, overflow: 'hidden' }}>
                <div onClick={() => { setPeriod('month'); setShowMonthPicker(false); }} style={{ padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: period === 'month' ? '#1e3a5f' : '#374151', background: period === 'month' ? '#eff6ff' : '#fff' }}>This Month</div>
                {availableMonths.filter(m => m !== new Date().toISOString().slice(0,7)).map(m => {
                  const d = new Date(m + '-01T12:00:00');
                  const lbl = d.toLocaleDateString([], { month: 'long', year: 'numeric' });
                  return <div key={m} onClick={() => { setPeriod(m); setShowMonthPicker(false); }} style={{ padding: '8px 14px', fontSize: 12, cursor: 'pointer', color: period === m ? '#1e3a5f' : '#374151', background: period === m ? '#eff6ff' : '#fff', fontWeight: period === m ? 700 : 400 }}>{lbl}</div>;
                })}
              </div>
            )}
          </div>
          {['ytd', 'all'].map(val => {
            const lbl = val === 'ytd' ? 'Year to Date' : 'All Time';
            return <button key={val} onClick={() => { setPeriod(val); setShowMonthPicker(false); }} style={{ padding: '5px 12px', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 11, fontWeight: 700, background: period === val ? 'rgba(255,255,255,.2)' : 'rgba(255,255,255,.07)', color: period === val ? '#fff' : 'rgba(255,255,255,.5)' }}>{lbl}</button>;
          })}
          <button onClick={() => setShowClear(x => !x)} style={{ padding: '5px 10px', borderRadius: 7, cursor: 'pointer', fontSize: 10, fontWeight: 700, background: showClear ? 'rgba(255,80,80,.3)' : 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.15)', color: showClear ? '#fca5a5' : 'rgba(255,255,255,.4)' }}>
            {showClear ? '▲' : '🗑'}
          </button>
        </div>
      </div>

      {/* Clear panel */}
      {showClear && (
        <div style={{ padding: '12px 16px', background: B.redBg, borderBottom: `1px solid ${B.redBorder}` }}>
          <div style={{ color: B.red, fontWeight: 700, fontSize: 12, marginBottom: 8 }}>Clear OPH History</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={() => { if (window.confirm('Delete ALL OPH performance history? This cannot be undone.')) { onClearAll?.(); setShowClear(false); } }} style={{ padding: '7px 14px', background: B.red, color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Clear All</button>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {allDates.slice(0, 8).map(d => (
                <button key={d} onClick={() => { if (window.confirm(`Delete history for ${d}?`)) onClearDate?.(d); }} style={{ padding: '5px 10px', background: B.white, color: B.red, border: `1px solid ${B.redBorder}`, borderRadius: 7, fontSize: 11, cursor: 'pointer' }}>
                  {new Date(d + 'T12:00:00').toLocaleDateString([], { month: 'short', day: 'numeric' })} ✕
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* No data */}
      {(!history || history.length === 0) ? (
        <div style={{ padding: '20px 16px', textAlign: 'center', color: B.textLight, fontSize: 13 }}>
          No history yet — save a day's line performance to start tracking.
        </div>
      ) : rows.length === 0 ? (
        <div style={{ padding: '16px', textAlign: 'center', color: B.textLight, fontSize: 13 }}>No data for {periodLabel().toLowerCase()}.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '28px 2fr .7fr 1fr 1fr 1fr 1fr 36px', background: B.rowAlt, borderBottom: `1px solid ${B.rowSep}` }}>
            {['#', 'Packer / Line', 'Days', 'Avg OPH', 'Best OPH', 'Orders', 'Hrs', ''].map((h, i) => (
              <div key={i} style={{ padding: '7px 8px', fontSize: 10, fontWeight: 700, letterSpacing: .5, color: B.textLight, textTransform: 'uppercase', textAlign: i >= 2 ? 'center' : 'left' }}>{h}</div>
            ))}
          </div>
          {rows.map((g, i) => {
            const medal = ['🥇', '🥈', '🥉'][i] || '';
            const atGoal = g.avgOPH >= OPH_GOAL;
            return (
              <div key={g.name} style={{ display: 'grid', gridTemplateColumns: '28px 2fr .7fr 1fr 1fr 1fr 1fr 36px', alignItems: 'center', background: i === 0 ? '#fefce8' : i % 2 === 0 ? B.white : B.rowAlt, borderBottom: `1px solid ${B.rowSep}`, borderLeft: i === 0 ? '3px solid #eab308' : '3px solid transparent' }}>
                <div style={{ padding: '9px 6px', textAlign: 'center', fontSize: 13 }}>{medal || i + 1}</div>
                <div style={{ padding: '9px 8px', fontWeight: 700, fontSize: 12, color: B.textDark }}>{g.name}</div>
                <div style={{ padding: '9px', textAlign: 'center', fontSize: 11, color: B.textLight }}>{g.days}</div>
                <div style={{ padding: '9px', textAlign: 'center' }}>
                  <span style={{ fontWeight: 800, fontSize: 14, color: atGoal ? B.green : B.amber }}>{g.avgOPH}</span>
                  <span style={{ fontSize: 9, color: B.textLight, marginLeft: 2 }}>oph</span>
                </div>
                <div style={{ padding: '9px', textAlign: 'center' }}>
                  <span style={{ fontWeight: 700, fontSize: 12, color: B.green }}>{g.bestOPH}</span>
                  <span style={{ fontSize: 9, color: B.textLight, marginLeft: 2 }}>oph</span>
                </div>
                <div style={{ padding: '9px', textAlign: 'center', fontSize: 12, color: B.textMid }}>{g.totalOrders}</div>
                <div style={{ padding: '9px', textAlign: 'center', fontSize: 12, color: B.textMid }}>{g.totalHrs} hrs</div>
                <div style={{ padding: '5px 3px', textAlign: 'center' }}>
                  {onDeleteEntry && (
                    <button onClick={() => { if (window.confirm(`Remove all history for ${g.name}?`)) onDeleteEntry(g.name); }} style={{ background: 'none', border: `1px solid ${B.redBorder}`, borderRadius: 4, color: B.red, fontSize: 9, fontWeight: 700, cursor: 'pointer', padding: '2px 5px' }}>✕</button>
                  )}
                </div>
              </div>
            );
          })}
          <div style={{ padding: '7px 14px', background: B.rowAlt, fontSize: 11, color: B.textLight, borderTop: `1px solid ${B.rowSep}` }}>
            {filtered.length} session{filtered.length !== 1 ? 's' : ''} · {rows.length} packer{rows.length !== 1 ? 's' : ''} · {periodLabel()}
          </div>
        </div>
      )}
    </Card>
  );
}

// ─── PROCESSING PLANNER TAB ───────────────────────────────────────────────────
const DEFAULT_MACHINES = ['T305', 'C500'];

function makeProcessingItem(o = {}) {
  return {
    id: `pi-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
    product: '', itemNumber: '', qtyToPull: '', qtyCompleted: '', totesProjected: '', ...o
  };
}

function makeProcessingDay() {
  return { machines: DEFAULT_MACHINES.map(m => ({ machine: m, items: [] })) };
}

function ProcessingPlannerTab({ processingAll, onProcessingAllChange }) {
  const allData = processingAll || {};
  const [selDate, setSelDate] = useState(todayISO());
  const [machineNames, setMachineNames] = useState(DEFAULT_MACHINES);
  const [editingMachines, setEditingMachines] = useState(false);
  const [newMachineName, setNewMachineName] = useState('');

  const day = allData[selDate] || makeProcessingDay();
  const machines = day.machines || DEFAULT_MACHINES.map(m => ({ machine: m, items: [] }));

  function setDay(updater) {
    onProcessingAllChange(all => {
      const cur = all[selDate] || makeProcessingDay();
      const next = typeof updater === 'function' ? updater(cur) : { ...cur, ...updater };
      return { ...all, [selDate]: next };
    });
  }

  function updateMachineItems(machineIdx, items) {
    setDay(d => {
      const mach = [...(d.machines || [])];
      mach[machineIdx] = { ...mach[machineIdx], items };
      return { ...d, machines: mach };
    });
  }

  function addItem(machineIdx) {
    const mach = machines[machineIdx];
    const items = [...(mach?.items || []), makeProcessingItem()];
    updateMachineItems(machineIdx, items);
  }

  function removeItem(machineIdx, itemId) {
    const items = (machines[machineIdx]?.items || []).filter(i => i.id !== itemId);
    updateMachineItems(machineIdx, items);
  }

  function updateItem(machineIdx, itemId, field, val) {
    const items = (machines[machineIdx]?.items || []).map(i => i.id === itemId ? { ...i, [field]: val } : i);
    updateMachineItems(machineIdx, items);
  }

  function addMachine() {
    if (!newMachineName.trim()) return;
    setDay(d => ({ ...d, machines: [...(d.machines || []), { machine: newMachineName.trim(), items: [] }] }));
    setNewMachineName('');
  }

  const savedDates = Object.keys(allData).sort().reverse();

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ color: B.navy, fontWeight: 700, fontSize: 15 }}>Processing Planner</div>
          <div style={{ color: B.textLight, fontSize: 12, marginTop: 2 }}>Assign products to processing machines · track pull and completion</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Inp type="date" value={selDate} onChange={e => setSelDate(e.target.value)} style={{ fontSize: 13, padding: '6px 10px', minWidth: 150 }} />
          {savedDates.length > 1 && (
            <select value={selDate} onChange={e => setSelDate(e.target.value)} style={{ padding: '6px 10px', borderRadius: 7, border: `1px solid ${B.cardBorder}`, fontSize: 13, color: B.textDark, outline: 'none' }}>
              {savedDates.map(d => <option key={d} value={d}>{new Date(d + 'T12:00:00').toLocaleDateString([], { month: 'short', day: 'numeric' })}</option>)}
            </select>
          )}
          <Btn variant="ghost" small onClick={() => setEditingMachines(e => !e)}>{editingMachines ? '✓ Done' : '⚙ Machines'}</Btn>
        </div>
      </div>

      {/* Machine editor */}
      {editingMachines && (
        <Card style={{ marginBottom: 14, padding: '12px 16px' }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: B.navy, marginBottom: 10 }}>Edit Machines</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            {machines.map((m, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, background: B.blueBg, border: `1px solid ${B.blueBorder}`, borderRadius: 6, padding: '4px 10px' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: B.blue }}>{m.machine}</span>
                <button onClick={() => { if (window.confirm(`Remove ${m.machine}?`)) setDay(d => ({ ...d, machines: d.machines.filter((_, mi) => mi !== i) })); }} style={{ background: 'none', border: 'none', color: B.textLight, cursor: 'pointer', fontSize: 12, padding: 0 }}>✕</button>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Inp value={newMachineName} onChange={e => setNewMachineName(e.target.value)} placeholder="Machine name e.g. T400" onKeyDown={e => e.key === 'Enter' && addMachine()} style={{ maxWidth: 200 }} />
            <Btn variant="primary" small onClick={addMachine}>+ Add</Btn>
          </div>
        </Card>
      )}

      {/* Per-machine tables */}
      {machines.map((mach, mi) => {
        const items = mach.items || [];
        const totalQtyPull = items.reduce((s, i) => s + (parseFloat(i.qtyToPull) || 0), 0);
        const totalTotes = items.reduce((s, i) => s + (parseFloat(i.totesProjected) || 0), 0);
        const totalCompleted = items.reduce((s, i) => s + (parseFloat(i.qtyCompleted) || 0), 0);
        const pctDone = totalQtyPull > 0 ? Math.round(totalCompleted / totalQtyPull * 100) : null;

        return (
          <Card key={mach.machine} style={{ marginBottom: 14 }}>
            <div style={{ background: B.navy, padding: '10px 16px', borderRadius: '10px 10px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ color: '#fff', fontWeight: 800, fontSize: 15 }}>{mach.machine}</div>
                {items.length > 0 && <div style={{ color: 'rgba(255,255,255,.45)', fontSize: 11, marginTop: 1 }}>{items.length} product{items.length !== 1 ? 's' : ''} · {totalQtyPull} units to pull · {totalTotes} totes</div>}
              </div>
              {pctDone != null && (
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: pctDone >= 100 ? '#86efac' : '#fdba74', fontWeight: 800, fontSize: 18 }}>{pctDone}%</div>
                  <div style={{ color: 'rgba(255,255,255,.4)', fontSize: 10 }}>complete</div>
                </div>
              )}
            </div>

            {/* Table */}
            <div style={{ overflowX: 'auto' }}>
              {items.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 28px', background: '#1e3a5f' }}>
                  {['Product', 'Item #', 'Qty to Pull', 'Completed', 'Totes', ''].map((h, i) => (
                    <div key={i} style={{ padding: '7px 10px', color: 'rgba(255,255,255,.5)', fontSize: 10, fontWeight: 700, letterSpacing: .5, textTransform: 'uppercase' }}>{h}</div>
                  ))}
                </div>
              )}
              {items.map((item, ri) => {
                const pulled = parseFloat(item.qtyToPull) || 0;
                const done = parseFloat(item.qtyCompleted) || 0;
                const remaining = pulled > 0 ? Math.max(0, pulled - done) : null;
                const itemPct = pulled > 0 && done > 0 ? Math.round(done / pulled * 100) : null;
                return (
                  <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 28px', alignItems: 'center', background: ri % 2 === 0 ? B.white : B.rowAlt, borderBottom: `1px solid ${B.rowSep}` }}>
                    <div style={{ padding: '7px 8px' }}>
                      <Inp value={item.product} placeholder="Product name" style={{ fontSize: 12, padding: '5px 7px' }} onChange={e => updateItem(mi, item.id, 'product', e.target.value)} />
                    </div>
                    <div style={{ padding: '7px 6px' }}>
                      <Inp value={item.itemNumber} placeholder="CP0000" style={{ fontSize: 12, padding: '5px 7px', fontFamily: 'monospace' }} onChange={e => updateItem(mi, item.id, 'itemNumber', e.target.value)} />
                    </div>
                    <div style={{ padding: '7px 6px' }}>
                      <Inp type="number" value={item.qtyToPull} placeholder="0" style={{ fontSize: 12, padding: '5px 7px', textAlign: 'center' }} onChange={e => updateItem(mi, item.id, 'qtyToPull', e.target.value)} />
                    </div>
                    <div style={{ padding: '7px 6px' }}>
                      <Inp type="number" value={item.qtyCompleted} placeholder="0" style={{ fontSize: 12, padding: '5px 7px', textAlign: 'center' }} onChange={e => updateItem(mi, item.id, 'qtyCompleted', e.target.value)} />
                    </div>
                    <div style={{ padding: '7px 6px', textAlign: 'center' }}>
                      <Inp type="number" value={item.totesProjected} placeholder="0" style={{ fontSize: 12, padding: '5px 7px', textAlign: 'center' }} onChange={e => updateItem(mi, item.id, 'totesProjected', e.target.value)} />
                    </div>
                    <div style={{ padding: '0 4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <button onClick={() => removeItem(mi, item.id)} style={{ background: 'none', border: 'none', color: B.textLight, cursor: 'pointer', fontSize: 13, padding: 2 }}>✕</button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Totals + add */}
            <div style={{ padding: '8px 12px', borderTop: items.length > 0 ? `1px solid ${B.rowSep}` : 'none' }}>
              {items.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 28px', padding: '6px 0', marginBottom: 8 }}>
                  <div style={{ padding: '0 2px', fontWeight: 800, fontSize: 12, color: B.navy }}>Totals</div>
                  <div />
                  <div style={{ textAlign: 'center', fontWeight: 800, fontSize: 13, color: B.navy }}>{totalQtyPull || '—'}</div>
                  <div style={{ textAlign: 'center', fontWeight: 800, fontSize: 13, color: totalCompleted > 0 ? B.green : B.textLight }}>{totalCompleted || '—'}</div>
                  <div style={{ textAlign: 'center', fontWeight: 800, fontSize: 13, color: B.navy }}>{totalTotes || '—'}</div>
                  <div />
                </div>
              )}
              <button onClick={() => addItem(mi)} style={{ width: '100%', padding: '8px', background: B.rowAlt, border: `1.5px dashed ${B.cardBorder}`, borderRadius: 7, color: B.textMid, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                + Add Product to {mach.machine}
              </button>
            </div>
          </Card>
        );
      })}

      {machines.length === 0 && (
        <Card style={{ padding: '32px 20px', textAlign: 'center' }}>
          <div style={{ color: B.textMid, fontSize: 14, marginBottom: 12 }}>No machines configured</div>
          <Btn variant="primary" onClick={() => setEditingMachines(true)}>⚙ Add Machines</Btn>
        </Card>
      )}
    </div>
  );
}

// ─── OPH HISTORY TAB ─────────────────────────────────────────────────────────
// ─── EMAIL TAB ────────────────────────────────────────────────────────────────
function EmailTab({ taktAll, processingAll, opsNotes, dayPlan, onDayPlanChange }) {
  const [copied, setCopied] = useState(false);
  const [eodMode, setEodMode] = useState(false);
  const today = todayISO();
  const dateLabel = fmtDate(today);
  const SEP = '═'.repeat(32);
  const sep = '─'.repeat(32);

  const taktDay = taktAll?.[today] || makeTaktDay();
  const processingDay = processingAll?.[today] || makeProcessingDay();
  const lines = taktDay.lines || [];
  const notes = opsNotes || taktDay.notes || '';

  // TAKT summary
  const totalOrders = lines.reduce((s, l) => s + (parseFloat(l.orderspacked) || 0), 0);
  const totalHrs = lines.reduce((s, l) => s + (parseFloat(l.duration) || 0), 0);
  const lineOPHs = lines.map(l => {
    const o = parseFloat(l.orderspacked) || 0;
    const h = parseFloat(l.duration) || 0;
    return o > 0 && h > 0 ? r2(o / h) : null;
  }).filter(v => v !== null);
  const teamOPH = lineOPHs.length > 0 ? r2(lineOPHs.reduce((s, v) => s + v, 0) / lineOPHs.length) : null;

  const taktLines = lines.length > 0
    ? [
        `  Team: ${totalOrders} orders  |  ${r2(totalHrs)} hrs  |  ${teamOPH != null ? teamOPH + ' OPH' : '—'}`,
        '',
        ...lines.filter(l => l.name.trim()).map(l => {
          const o = parseFloat(l.orderspacked) || 0;
          const h = parseFloat(l.duration) || 0;
          const oph = o > 0 && h > 0 ? r2(o / h) : null;
          return `  • ${l.name} — ${o} orders${h > 0 ? ` · ${h} hrs` : ''}${oph != null ? ` → ${oph} OPH` : ''}`;
        })
      ]
    : ['  • No packer data logged'];

  // Processing summary
  const processingLines = [];
  (processingDay.machines || []).forEach(mach => {
    const items = mach.items || [];
    if (!items.length) return;
    processingLines.push(`  ▸ ${mach.machine}`);
    items.forEach(item => {
      if (!item.product) return;
      const pulled = parseFloat(item.qtyToPull) || 0;
      const done = parseFloat(item.qtyCompleted) || 0;
      const remaining = pulled > 0 ? Math.max(0, pulled - done) : null;
      const totes = parseFloat(item.totesProjected) || 0;
      let line = `    • ${item.product}`;
      if (item.itemNumber) line += ` (${item.itemNumber})`;
      if (pulled > 0) line += `  |  Pull: ${pulled}`;
      if (done > 0) line += `  |  Done: ${done}`;
      if (remaining != null) line += `  |  Remaining: ${remaining}`;
      if (totes > 0) line += `  |  Totes: ${totes}`;
      processingLines.push(line);
    });
  });
  if (!processingLines.length) processingLines.push('  • No processing data logged');

  const emailBody = [
    eodMode ? 'CrabPlace — Fulfillment Ops Recap' : 'CrabPlace — Fulfillment Ops Plan',
    dateLabel, SEP, '',
    ...(dayPlan && dayPlan.trim() ? ['📅 PLAN FOR THE DAY', sep, `  ${dayPlan.trim()}`, ''] : []),
    '📋 ORDER FULFILLMENT TAKT', sep,
    ...taktLines, '',
    '🏭 PROCESSING PLANNER', sep,
    ...processingLines, '',
    ...(notes ? ['📝 NOTES', sep, `  ${notes}`, ''] : []),
    SEP,
    `Generated: ${new Date().toLocaleString()}`
  ].join('\n');

  function doCopy() {
    navigator.clipboard.writeText(emailBody).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2500);
    }).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = emailBody; ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta); ta.select(); document.execCommand('copy');
      document.body.removeChild(ta); setCopied(true); setTimeout(() => setCopied(false), 2500);
    });
  }

  function doMailto() {
    const subject = encodeURIComponent(`CrabPlace Fulfillment Ops ${eodMode ? 'Recap' : 'Plan'} — ${dateLabel}`);
    navigator.clipboard.writeText(emailBody).catch(() => {});
    window.open(`mailto:?subject=${subject}`);
  }

  return (
    <div>
      <div style={{ background: B.navy, borderRadius: 12, padding: '14px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>Daily Fulfillment Recap</div>
          <div style={{ color: 'rgba(255,255,255,.5)', fontSize: 11, marginTop: 2 }}>{dateLabel} · copy to email or clipboard</div>
        </div>
        <div style={{ display: 'flex', borderRadius: 7, overflow: 'hidden', border: '1px solid rgba(255,255,255,.25)', fontSize: 11, fontWeight: 700 }}>
          <button onClick={() => setEodMode(false)} style={{ padding: '5px 12px', background: !eodMode ? 'rgba(255,255,255,.9)' : 'rgba(255,255,255,.1)', color: !eodMode ? B.navy : 'rgba(255,255,255,.6)', border: 'none', cursor: 'pointer', fontWeight: 700 }}>Daily Plan</button>
          <button onClick={() => setEodMode(true)} style={{ padding: '5px 12px', background: eodMode ? 'rgba(255,255,255,.9)' : 'rgba(255,255,255,.1)', color: eodMode ? B.navy : 'rgba(255,255,255,.6)', border: 'none', cursor: 'pointer', fontWeight: 700 }}>EOD Recap</button>
        </div>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <CardHeader sub="Included at the top of the email — write it in Brenda's voice">Plan for the Day</CardHeader>
        <div style={{ padding: '10px 16px 14px' }}>
          <textarea
            rows={3}
            value={dayPlan || ''}
            onChange={e => onDayPlanChange?.(e.target.value)}
            placeholder="e.g. Plans today include grading in the AM. Packing in the PM. Robert and Telly on crabs while the rest of the team handles boxes."
            style={{
              width: '100%', padding: '9px 11px', borderRadius: 8,
              border: `1.5px solid ${B.cardBorder}`, fontSize: 13,
              color: B.textDark, resize: 'vertical', lineHeight: 1.6,
              outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit'
            }}
          />
          <div style={{ fontSize: 11, color: B.textLight, marginTop: 5 }}>
            This appears above the TAKT data in the copied email text.
          </div>
        </div>
      </Card>

      <div style={{ background: '#fafaf8', borderRadius: 8, padding: '14px 16px', border: `1px solid ${B.cardBorder}`, marginBottom: 16, overflowX: 'auto' }}>
        <pre style={{ fontFamily: "'Courier New','Lucida Console',monospace", fontSize: 11.5, lineHeight: 1.75, color: B.textDark, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
          {emailBody}
        </pre>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button onClick={doCopy} style={{ flex: 1, minHeight: 46, background: copied ? B.green : B.navy, color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'background .2s' }}>
          {copied ? '✓ Copied!' : '📋 Copy to Clipboard'}
        </button>
        <button onClick={doMailto} style={{ flex: 1, minHeight: 46, background: B.white, color: B.navy, border: `1.5px solid ${B.navy}`, borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          ✉ Open in Mail App
        </button>
      </div>
    </div>
  );
}

// ─── NEON QUERIES ─────────────────────────────────────────────────────────────
// Uses fulfillment_ops_state_save / fulfillment_ops_state_load_latest
// You'll need to add these to neon.js (see comment below)
async function loadFulfillmentState() {
  try {
    const res = await neonQuery('fulfillment_ops_state_load_latest', {});
    const row = Array.isArray(res) ? res[0] : (res?.rows?.[0] ?? null);
    return row?.state ?? null;
  } catch (err) {
    console.warn('[FulfillmentOps] Neon load failed:', err);
    return null;
  }
}

async function saveFulfillmentState(state) {
  try {
    await neonQuery('fulfillment_ops_state_save', { plan_date: todayISO(), state });
  } catch (err) {
    console.warn('[FulfillmentOps] Neon save failed:', err);
  }
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function FulfillmentOps() {
  const [tab, setTab] = useState('snapshot');
  const [neonLoaded, setNeonLoaded] = useState(false);

  // Fulfillment-specific state (separate Neon table)
  const [taktAll, setTaktAll] = useState({});
  const [taktHistory, setTaktHistory] = useState([]);
  // Written by the packing floor (lib/takt-autofill.js), never by this page.
  // ⚠️ Deliberately NOT included in the save payload below — the server carries
  // it forward on every save instead. Adding it here would hand a page that
  // autosaves every 3 seconds the power to overwrite the measured numbers with
  // whatever it happened to load minutes ago.
  const [taktAuto, setTaktAuto] = useState({});
  const [processingAll, setProcessingAll] = useState({});
  const [opsNotes, setOpsNotes] = useState('');
  const [dayPlan, setDayPlan] = useState('');

  const saveTimerRef = useRef(null);
  const today = todayISO();

  // Load from Neon on mount
  useEffect(() => {
    loadFulfillmentState().then(state => {
      if (state) {
        if (state.taktAll) setTaktAll(state.taktAll);
        if (state.taktHistory) setTaktHistory(normalizeTaktHistory(state.taktHistory));
        if (state.taktAuto) setTaktAuto(state.taktAuto);
        if (state.processingAll) setProcessingAll(state.processingAll);
        if (state.opsNotes?.[today]) setOpsNotes(state.opsNotes[today]);
        if (state.dayPlan?.[today]) setDayPlan(state.dayPlan[today]);
      }
    }).finally(() => setNeonLoaded(true));
  }, [today]);

  // Debounced auto-save
  useEffect(() => {
    if (!neonLoaded) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveFulfillmentState({ taktAll, taktHistory, processingAll, opsNotes: { [today]: opsNotes }, dayPlan: { [today]: dayPlan } });
    }, 3000);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [taktAll, taktHistory, processingAll, opsNotes, dayPlan, neonLoaded, today]);

  function doSaveNow() {
    saveFulfillmentState({ taktAll, taktHistory, processingAll, opsNotes: { [today]: opsNotes }, dayPlan: { [today]: dayPlan } });
  }

  function handleProcessingChange(updater) {
    setProcessingAll(prev => typeof updater === 'function' ? updater(prev) : updater);
  }

  return (
    <div style={{ padding: '20px 16px', maxWidth: 900, margin: '0 auto', paddingBottom: 48, fontFamily: "'Segoe UI',system-ui,sans-serif", color: B.textDark }}>
      {/* Page header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ color: B.navy, fontWeight: 800, fontSize: 22 }}>Ops Planner</div>
        <div style={{ color: B.textLight, fontSize: 13, marginTop: 3 }}>
          Fulfillment TAKT · Processing · Daily Snapshot · OPH tracking
        </div>
      </div>

      <OpsTabBar tab={tab} onChange={setTab} />

      {tab === 'snapshot' && <DailySnapshotTab />}
      {tab === 'takt' && (
        <>
          <FulfillmentTaktTab
            taktAll={taktAll}
            onTaktAllChange={setTaktAll}
            taktHistory={taktHistory}
            onHistoryChange={setTaktHistory}
            onSaveNow={doSaveNow}
            taktAuto={taktAuto}
          />
          <OphHistoryInline
            history={taktHistory}
            onClearAll={() => setTaktHistory([])}
            onClearDate={date => setTaktHistory(h => h.filter(r => r.date !== date))}
            onDeleteEntry={name => setTaktHistory(h => h.filter(r => canonicalFloorName(r.name) !== name))}
          />
        </>
      )}
      {tab === 'processing' && (
        <ProcessingPlannerTab
          processingAll={processingAll}
          onProcessingAllChange={handleProcessingChange}
        />
      )}
      {tab === 'email' && (
        <EmailTab
          taktAll={taktAll}
          processingAll={processingAll}
          opsNotes={opsNotes}
          dayPlan={dayPlan}
          onDayPlanChange={setDayPlan}
        />
      )}
    </div>
  );
}
